import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { APP_DEFAULT_LANDING_PATH } from '../constants/app-screens.constants';
import type { SessionUserDto } from '../dtos/session/session-user.dto';
import { OptionMenuEnum } from '../enums/option-menu.enum';
import { ScreenEnum } from '../enums/screen.enum';
import { Language } from '../models/language.model';
import { LocalPermission } from '../models/local-permission.model';
import { MenuOption } from '../models/menu-option.model';
import { PermissionsScreen } from '../models/permission-screen';
import { Person } from '../models/person.model';
import { User } from '../models/user.model';
import { UserType } from '../models/user-type.model';
import { UserPermissionView } from '../models/user-permission-view.model';
import {
  mapMenuOptionDto,
  UserPermissionsResponseDto,
} from '../dtos/user/user-permissions-response.dto';

/**
 * Configuración y estado del usuario autenticado.
 *
 * Responsabilidades:
 * - Guardar estado de sesión/usuario/permisos/menú/home/screenDetails.
 * - Proveer helpers de autorización (hasAccess/hasPermission).
 * - Estado de menú/permisos: sin petición automática a `user/permissions` (ver `markAppReadyWithoutPermissions`).
 *
 * Nota:
 * - Este servicio NO administra i18n/idioma del sistema (eso vive en SystemConfigService).
 */
@Injectable({ providedIn: 'root' })
export class UserConfigService {
  private readonly loadedPermissionsSubject = new BehaviorSubject<boolean>(false);
  readonly loadedPermissions$ = this.loadedPermissionsSubject.asObservable();

  private readonly userSubject = new BehaviorSubject<User>(new User({}));
  readonly user$ = this.userSubject.asObservable();

  private readonly permissionsSubject = new BehaviorSubject<LocalPermission[]>([]);
  readonly permissions$ = this.permissionsSubject.asObservable();

  private readonly screenDetailsSubject = new BehaviorSubject<UserPermissionView[]>([]);
  readonly screenDetails$ = this.screenDetailsSubject.asObservable();

  private readonly menuOptionsSubject = new BehaviorSubject<MenuOption[]>([]);
  readonly menuOptions$ = this.menuOptionsSubject.asObservable();

  private readonly homeMenuSubject = new BehaviorSubject<MenuOption[]>([]);
  readonly homeMenu$ = this.homeMenuSubject.asObservable();

  private readonly activePathSubject = new BehaviorSubject<string>('');
  readonly activePath$ = this.activePathSubject.asObservable();

  private readonly currentScreenSubject = new BehaviorSubject<MenuOption>(new MenuOption());
  readonly currentScreen$ = this.currentScreenSubject.asObservable();

  /** Pantallas permitidas (ids: dashboard, compras, clientes, …) tras `applySessionBootstrap`. */
  private readonly allowedScreenIds = new Set<string>();

  get loadedPermissions(): boolean {
    return this.loadedPermissionsSubject.value;
  }

  get user(): User {
    return this.userSubject.value;
  }

  get permissions(): LocalPermission[] {
    return this.permissionsSubject.value;
  }

  get screenDetails(): UserPermissionView[] {
    return this.screenDetailsSubject.value;
  }

  get menuOptions(): MenuOption[] {
    return this.menuOptionsSubject.value;
  }

  get homeMenu(): MenuOption[] {
    return this.homeMenuSubject.value;
  }

  get activePath(): string {
    return this.activePathSubject.value;
  }

  get currentScreen(): MenuOption {
    return this.currentScreenSubject.value;
  }

  /**
   * URL de destino tras login.
   */
  getDefaultLandingUrl(): string {
    return APP_DEFAULT_LANDING_PATH;
  }

  /**
   * Aplica sesión desde bela-belleza-api (`GET user/me`).
   */
  applySessionBootstrap(dto: SessionUserDto): void {
    if (!dto?.allowed) {
      this.clearUserState();
      return;
    }

    const user = new User({
      id: dto.userId,
      userSessionId: dto.userId,
      userTypeId: dto.tipoUsuarioId,
      person: new Person({ name: dto.name }),
      userType: new UserType({
        id: dto.tipoUsuarioId,
        name: dto.tipoUsuarioName,
      }),
    });
    this.userSubject.next(user);

    const menuOptions = (dto.screens ?? []).map(
      (s, index) =>
        new MenuOption({
          screenId: index + 1,
          key: s.id,
          path: s.path,
          title: s.label,
          moduleType: OptionMenuEnum.Screen,
          icon: this.iconForAppScreen(s.id),
          order: index,
        }),
    );
    this.menuOptionsSubject.next(menuOptions);
    this.allowedScreenIds.clear();
    (dto.screens ?? []).forEach((s) => this.allowedScreenIds.add(s.id));
    this.permissionsSubject.next([]);
    // Sin API de permisos granular: el administrador (tipo 1) obtiene control total en pantalla Usuarios.
    const isAdmin = dto.tipoUsuarioId === 1;
    if (isAdmin) {
      const usersAll = new UserPermissionView();
      usersAll.screenKeyCode = ScreenEnum.Users;
      usersAll.permissionKeyCode = 'TI';
      this.screenDetailsSubject.next([usersAll]);
    } else {
      this.screenDetailsSubject.next([]);
    }
    this.rebuildHomeMenu();
    this.loadedPermissionsSubject.next(true);
  }

  /** Si la ruta declara `data.screenId`, el usuario debe tener esa pantalla en sesión. */
  hasScreenAccess(screenId: string): boolean {
    return this.allowedScreenIds.has(screenId);
  }

  private iconForAppScreen(id: string): string {
    switch (id) {
      case 'dashboard':
        return 'dashboard';
      case 'compras':
        return 'shopping_cart';
      case 'clientes':
        return 'people';
      case 'usuarios':
        return 'manage_accounts';
      case 'sorteo':
        return 'emoji_events';
      case 'promociones':
        return 'local_offer';
      default:
        return 'circle';
    }
  }

  /**
   * Actualiza únicamente el usuario (sin tocar menús/permisos).
   * Útil para compatibilidad cuando hay código legacy que llama `SystemService.setUser(...)`.
   */
  setUser(user: User): void {
    this.userSubject.next(user);
  }

  /**
   * Marca la zona `/app` como lista sin llamar al API de permisos.
   * Menú, usuario y `screenDetails` quedan como están (vacíos hasta `applyPermissionsResponse` si lo usas en otro flujo).
   */
  markAppReadyWithoutPermissions(): void {
    this.loadedPermissionsSubject.next(true);
  }

  /**
   * Aplica la respuesta del endpoint `user/permissions` al estado del portal.
   * Contrato esperado (actual):
   * - permissions, user (con menuOptions)
   */
  applyPermissionsResponse(dto: UserPermissionsResponseDto): void {
    if (!dto) {
      this.clearUserState();
      return;
    }

    // Permisos (tabla/acciones)
    this.permissionsSubject.next((dto.permissions ?? []).map((id) => new LocalPermission({ id })));

    // Usuario
    const userDto = dto.user;
    const user = new User({
      id: userDto.userId,
      userSessionId: userDto.userSessionId ?? userDto.userId,
      account: userDto.account,
      resetPassword: userDto.resetPassword,
      userTypeId: userDto.userTypeId,
      language: userDto.language ? new Language({ keyCode: userDto.language }) : undefined,
      userType: userDto.userType
        ? new UserType({
            id: userDto.userType.userTypeId,
            name: userDto.userType.name,
            permanentSession: userDto.userType.isSessionPermanent,
            sessionTime: userDto.userType.sessionTime,
            inactivityTime: userDto.userType.inactivityTime ?? 0,
            regexPassword: (userDto.userType.regex ?? '').trim(),
            regexComment: (userDto.userType.regexComment ?? '').trim(),
          })
        : undefined,
      person: new Person({ name: userDto.name }),
      photo: userDto.photo ?? null,
      status: userDto.status ?? null,
    });

    // Sesión enmascarada (en el DTO viene como flags/strings, no como objetos completos).
    if (userDto.isMasked) {
      user.maskedUser = new User({ person: new Person({ name: userDto.name }) });
      user.currentUser = new User({ person: new Person({ name: userDto.userName }) });
    }
    this.userSubject.next(user);

    // Menú / pantallas
    const menuOptions = (userDto.menuOptions ?? []).map(mapMenuOptionDto) as MenuOption[];
    this.menuOptionsSubject.next(menuOptions);

    // Detalle (screenId, permissionId) para hasPermission(screen, permission)
    const screenDetails = (dto.screenDetails ?? []).map((sd) => ({
      screenId: sd.screenId,
      screenKeyCode: sd.screenKey,
      permissionId: sd.permissionId,
      permissionKeyCode: sd.permissionKey,
    })) as UserPermissionView[];
    this.screenDetailsSubject.next(screenDetails);

    // Home menu
    this.rebuildHomeMenu();

    this.loadedPermissionsSubject.next(true);
  }

  /**
   * Actualiza solo la ruta de la foto del usuario sin recargar todos los permisos.
   * Útil cuando se actualiza o elimina la foto de perfil.
   */
  updateUserPhoto(photo: string | null): void {
    const currentUser = this.userSubject.value;
    if (currentUser) {
      const updatedUser = new User({
        id: currentUser.id,
        userSessionId: currentUser.userSessionId,
        account: currentUser.account,
        resetPassword: currentUser.resetPassword,
        userTypeId: currentUser.userTypeId,
        userType: currentUser.userType,
        person: currentUser.person,
        photo: photo,
        maskedUser: currentUser.maskedUser,
        currentUser: currentUser.currentUser,
      });
      this.userSubject.next(updatedUser);
    }
  }

  clearUserState(): void {
    this.loadedPermissionsSubject.next(false);
    this.userSubject.next(new User({}));
    this.permissionsSubject.next([]);
    this.screenDetailsSubject.next([]);
    this.menuOptionsSubject.next([]);
    this.homeMenuSubject.next([]);
    this.activePathSubject.next('');
    this.currentScreenSubject.next(new MenuOption());
    this.allowedScreenIds.clear();
  }

  /**
   * Construye el menú de Home a partir del menú completo (sidebar).
   */
  rebuildHomeMenu(): void {
    const menuOptions = this.menuOptionsSubject.value ?? [];
    let homeMenu: MenuOption[] = [];

    homeMenu = homeMenu.concat(menuOptions.filter((item) => item.homeScreen));

    menuOptions.forEach((optionA) => {
      if (optionA.subMenu?.length) {
        homeMenu = homeMenu.concat(optionA.subMenu.filter((x) => x.homeScreen));

        optionA.subMenu.forEach((optionB) => {
          if (optionB.subMenu?.length) {
            homeMenu = homeMenu.concat(optionB.subMenu.filter((x) => x.homeScreen));
          }
        });
      }
    });

    homeMenu.sort((a, b) => {
      if ((a.homeOrder ?? 0) > (b.homeOrder ?? 0)) return 1;
      if ((a.homeOrder ?? 0) < (b.homeOrder ?? 0)) return -1;
      return 0;
    });

    this.homeMenuSubject.next(homeMenu);
  }

  /**
   * Marca como activa la pantalla actual (para breadcrumb/menú activo).
   */
  activateOptionMenu(url: string): void {
    const menuOptions = this.menuOptionsSubject.value ?? [];
    let activePath = '';
    let currentScreen: MenuOption | null = null;

    menuOptions.forEach((optionA) => {
      if (optionA.path === url) {
        optionA.isActive = true;
        activePath = optionA.title ?? '';
        currentScreen = optionA;
      } else {
        optionA.isActive = false;
      }

      optionA.subMenu?.forEach((optionB) => {
        if (optionB.path === url) {
          optionB.isActive = true;
          optionA.isActive = true;
          activePath = `${optionA.title} > ${optionB.title}`;
          currentScreen = optionB;
        } else {
          optionB.isActive = false;
        }

        optionB.subMenu?.forEach((optionC) => {
          if (optionC.path === url) {
            optionC.isActive = true;
            optionB.isActive = true;
            optionA.isActive = true;
            activePath = `${optionA.title} > ${optionB.title} > ${optionC.title}`;
            currentScreen = optionC;
          } else {
            optionC.isActive = false;
          }
        });
      });
    });

    this.activePathSubject.next(activePath);
    this.currentScreenSubject.next(currentScreen ?? new MenuOption());

    // Emitir menú actualizado (para que OnPush/observables refresquen)
    this.menuOptionsSubject.next(menuOptions);
  }

  /** Devuelve los IDs de pantalla que el usuario tiene en su menú (recursivo). */
  getScreenIdsFromMenu(): number[] {
    const ids: number[] = [];
    const addFrom = (options: MenuOption[]): void => {
      (options ?? []).forEach((o) => {
        if (o.screenId != null && o.screenId > 0) ids.push(o.screenId);
        if (o.subMenu?.length) addFrom(o.subMenu);
      });
    };
    addFrom(this.menuOptionsSubject.value ?? []);
    return ids;
  }

  /** Devuelve las claves de pantalla (screenKey) del menú del usuario (recursivo). */
  getScreenKeysFromMenu(): string[] {
    const keys: string[] = [];
    const addFrom = (options: MenuOption[]): void => {
      (options ?? []).forEach((o) => {
        if (o.key != null && String(o.key).trim() !== '') keys.push(String(o.key).trim());
        if (o.subMenu?.length) addFrom(o.subMenu);
      });
    };
    addFrom(this.menuOptionsSubject.value ?? []);
    return keys;
  }

  // ------- Permisos / helpers -------
  hasAccess(screen: ScreenEnum): boolean {
    const screenKeyCodeStr = String(screen);
    return !!this.screenDetailsSubject.value.find((sd) => sd.screenKeyCode === screenKeyCodeStr);
  }

  hasPermission(screen: ScreenEnum, permission: string | string[], onlyTI: boolean = false): boolean {
    const requestedPermissions = Array.isArray(permission) ? permission : [permission];
    const screenDetails = this.screenDetails;
    const hasTI = screenDetails.some(
      (screenDetail) =>
        screenDetail.screenKeyCode == screen &&
        screenDetail.permissionKeyCode === 'TI'
    );

    if (onlyTI && !hasTI) {
      return false;
    }
    if (hasTI) {
      return true;
    }

    return screenDetails.some(
      (screenDetail) =>
        screenDetail.screenKeyCode == screen &&
        requestedPermissions.includes(String(screenDetail.permissionKeyCode))
    );
  }

  getPermissionByScreen(screen: ScreenEnum): PermissionsScreen {
    const permissions: PermissionsScreen = new PermissionsScreen({});

    permissions.manage = this.hasPermission(screen, 'Manage');
    permissions.read = this.hasPermission(screen, ['Manage', 'View']);
    permissions.add = this.hasPermission(screen, ['Manage', 'Add']);
    permissions.edit = this.hasPermission(screen, ['Manage', 'Edit']);
    permissions.delete = this.hasPermission(screen, ['Manage', 'Delete']);
    // Note: Export permission removed, use Manage or View as needed

    return permissions;
  }
}

